import * as React from 'react'
import {Component, FC, Fragment, useState} from 'react'
import {autorun} from 'mobx'
import {clone, pick, keys, isFunction, each} from 'lodash'
import {inject, Observer, observer} from 'mobx-react'
import cn from 'classnames'
import {parse} from 'qs'
import IMask from 'imask'
import {IMaskInput} from 'react-imask'
import {
  CreditCardIframeMessageType,
  getCreditCardType,
  Messenger,
  PaymentMethod,
} from '@raisenow/epik'
import {useStores} from '../../../../../lib/MobXProvider'
import {DynamicSlotName} from '../../../../../lib/DynamicSlotName'
import {PaymentForm} from '../../../../../lib/forms/PaymentForm'
import {SubscriptionRenewForm} from '../../../../../lib/forms/SubscriptionRenewForm'
import {WidgetApi} from '../../../../../lib/WidgetApi'
import {ErrorMessage} from '../../../../ui/ErrorMessage'
import {DynamicSlot} from '../../../../dynamic/DynamicSlot'
import {InfoToggle} from '../../../../ui/InfoToggle'
import {getCreditCardNumberMask} from '../../../../../lib/helpers'
import {IconInfo} from '../../../../ui/Icons'
import {InputWrapper} from '../../../../wrappers/InputWrapper'
import {DirtyWrapper} from '../../../../wrappers/DirtyWrapper'
import {WidgetFlag} from '../../../../../lib/WidgetFlag'
import {WidgetEventName} from '../../../../../lib/WidgetEventer'
import {RippleWrapper} from '../../../../wrappers/RippleWrapper'
import {InputCheckboxWrapper} from '../../../../wrappers/InputCheckableWrapper'

///////////////////////////////////////////////////////////////////////////////

export type CreditCardFormProps = {
  form: PaymentForm | SubscriptionRenewForm
}

export type CreditCardIframeProps = {
  api?: WidgetApi
  form: PaymentForm | SubscriptionRenewForm
}

export type CreditCardInsideIframeProps = {
  api?: WidgetApi
  form: PaymentForm | SubscriptionRenewForm
}

export type CreditCardLocalProps = {
  form: PaymentForm | SubscriptionRenewForm
}

///////////////////////////////////////////////////////////////////////////////

export const CreditCardForm: FC<CreditCardFormProps> = observer((props) => {
  const {api} = useStores<{api: WidgetApi}>()
  const {form} = props

  // show credit card iframe
  if (
    api.computedConfig.useCreditCardIframe &&
    !process.env.IS_CREDIT_CARD_IFRAME
  ) {
    return <CreditCardIframe form={form} />
  }

  // show form inside credit card iframe
  if (process.env.IS_CREDIT_CARD_IFRAME) {
    return <CreditCardInsideIframe form={form} />
  }

  // show regular form
  return <CreditCardLocal form={form} />
})

///////////////////////////////////////////////////////////////////////////////

@inject('api')
export class CreditCardIframe extends Component<CreditCardIframeProps> {
  constructor(props) {
    super(props)
  }

  componentDidMount() {
    let {api, form} = this.props
    let payment = form.getPayment()

    const init = async () => {
      let {messenger} = await payment.showCreditCardIframe(
        '#' + this.props.api.ui.getCreditCardIframeTargetId(),
      )

      messenger.on(
        CreditCardIframeMessageType.CREDIT_CARD_TYPE_CHANGED,
        async (ccType) => {
          form.setPaymentMethod(ccType)
          await api.events[WidgetEventName.PAYMENT_METHOD_CHANGED].publish({
            api,
          })
        },
      )

      // if user has pressed enter (submitted the form) inside iframe,
      // we must submit the form in main window
      messenger.on(CreditCardIframeMessageType.SUBMIT, async () => {
        await form.validateAndSend()
      })

      // if user has pressed enter (submitted the form) inside main window,
      // we must mark all fields in cc-iframe as touched and validate cc-iframe
      api.events[WidgetEventName.BEFORE_PAYMENT_VALIDATE_AND_SEND].subscribe(
        (e) => {
          messenger.emit(CreditCardIframeMessageType.MARK_ALL_FIELDS_AS_TOUCHED)
        },
      )
    }

    init()

    autorun(() => {
      // rerun on "language" change
      api.computedConfig.language

      payment.creditCardIframe?.messenger.emit(
        CreditCardIframeMessageType.SET_LANGUAGE,
        api.computedConfig.language,
      )
    })

    autorun(() => {
      // rerun on "language" change
      api.computedConfig.flow

      payment.creditCardIframe?.messenger.emit(
        CreditCardIframeMessageType.SET_FLOW,
        api.computedConfig.flow,
      )
    })
  }

  componentWillUnmount() {
    const {form} = this.props
    const payment = form.getPayment()

    payment.hideCreditCardIframe()
  }

  render() {
    const {form} = this.props

    return (
      <Fragment>
        {/* observe credit card form errors and place an invisible error placeholder
          in order for the "scroll to top error" functionality to work properly */}
        <Observer
          render={() => {
            const errors = form.validationErrors
            const relevantFields = ['cardno', 'expm', 'expy', 'cvv']
            const relevantErrors = pick(errors, relevantFields)

            return (
              <div className="sr-only">
                {keys(relevantErrors).map((errorKey, key) => (
                  <ErrorMessage
                    key={key}
                    errors={relevantErrors}
                    errorKey={errorKey}
                  />
                ))}
              </div>
            )
          }}
        />

        {/* credit card iframe wrapper must not be inside an observer,
          otherwise it will keep re-rendering */}
        <div
          id={this.props.api.ui.getCreditCardIframeTargetId()}
          className="credit-card-form-iframe"
        />
      </Fragment>
    )
  }
}

///////////////////////////////////////////////////////////////////////////////

@inject('api')
export class CreditCardInsideIframe extends Component<CreditCardInsideIframeProps> {
  token: string
  previousPaymentMethod: PaymentMethod
  subscriptions: any[] = []

  componentDidMount() {
    const {api, form} = this.props
    let {channel, debug: debugParam} = parse(window.location.search, {
      ignoreQueryPrefix: true,
    })
    const debug = debugParam === 'true'
    const relevantFields = ['cardno', 'expm', 'expy', 'cvv']

    const messenger = new Messenger({
      listenTo: window,
      emitTo: window.parent,
      label: 'iframe',
      channel: channel as string,
      debug,
    })

    // set flag to prevent default action (validateAndSend) on pressing Enter key
    api.flags.add(WidgetFlag.PREVENT_PAYMENT_VALIDATE_AND_SEND)

    // if user has pressed enter (submitted the form) inside iframe,
    // we must submit the form in main window
    this.subscriptions.push(
      api.events[WidgetEventName.BEFORE_PAYMENT_VALIDATE_AND_SEND].subscribe(
        (e) => {
          messenger.emit(CreditCardIframeMessageType.SUBMIT)
        },
      ),
    )

    // mark relevant fields as dirty
    messenger.on(CreditCardIframeMessageType.MARK_ALL_FIELDS_AS_TOUCHED, () => {
      form.dirtyFields = clone(relevantFields)
    })

    // respond whenever parent asks for credit card type
    // (called by acquireCreditCardTypeFromIframe Epik's method)
    messenger.respond(
      CreditCardIframeMessageType.DETECT_CREDIT_CARD_TYPE,
      () => {
        const {flow} = api.computedConfig

        return getCreditCardType(form.getPaymentConfig().cardno, flow)
      },
    )

    // respond whenever parent asks to validate
    messenger.respond(CreditCardIframeMessageType.VALIDATE, async () => {
      const errors = await form.validate(true)

      return pick(errors, relevantFields)
    })

    // respond whenever parent asks for credit card token
    messenger.respond(CreditCardIframeMessageType.TOKENIZE, async () => {
      const {flow} = api.computedConfig

      if (this.token) {
        messenger.emit(CreditCardIframeMessageType.TOKENIZE_SUCCESS, this.token)

        return this.token
      }

      try {
        const {cardno, expm, expy, cvv} = form.getPaymentConfig()
        const token = await api.epik.getCreditCardToken({
          flow,
          cardno,
          expm,
          expy,
          cvv,
        })

        messenger.emit(CreditCardIframeMessageType.TOKENIZE_SUCCESS, token)
        this.token = token

        return token
      } catch (error) {
        messenger.emit(CreditCardIframeMessageType.TOKENIZE_ERROR)
        this.token = null

        return null
      }
    })

    const trackFormChanges = () => {
      // observe form
      form.getPaymentConfig()

      const paymentMethod = form.getPaymentMethod()

      if (this.previousPaymentMethod !== paymentMethod) {
        this.previousPaymentMethod = paymentMethod

        // notify parent about cc-type changes
        messenger.emit(
          CreditCardIframeMessageType.CREDIT_CARD_TYPE_CHANGED,
          paymentMethod,
        )
      }
    }

    autorun(() => {
      trackFormChanges()

      // clear token on form data change
      this.token = null
    })
  }

  componentWillUnmount() {
    each(this.subscriptions, (f) => {
      if (isFunction(f)) {
        f()
      }
    })
  }

  render() {
    return <CreditCardLocal form={this.props.form} />
  }
}

///////////////////////////////////////////////////////////////////////////////

export const CreditCardLocal: FC<CreditCardLocalProps> = observer((props) => {
  const {api} = useStores<{api: WidgetApi}>()
  const {form} = props
  const formData = form.data
  const errors = form.validationErrors
  const {
    epik: {
      config: {skipCvv},
    },
  } = api

  const paymentMethod = form.getPaymentMethod()
  const cardno = formData.cardno || ''
  const cvv = formData.cvv || ''
  let expm = formData.expm || ''
  let expy = formData.expy || ''
  let exp = ''

  if (expm || expy) {
    exp = [expm, expy].join(' / ')
  }

  const setCardno = async (value) => {
    formData.cardno = value

    let ccType = getCreditCardType(value, form.getPaymentConfig().flow)
    ccType = ccType ? ccType : PaymentMethod.CREDIT_CARD

    if (paymentMethod !== ccType) {
      form.setPaymentMethod(ccType)
      await api.events[WidgetEventName.PAYMENT_METHOD_CHANGED].publish({api})
    }
  }
  const setCvv = (value) => (formData.cvv = value)
  const setExp = (value) => {
    formData.expm = expm = (value || '').substr(0, 2)
    formData.expy = expy = (value || '').substr(2, 2)
  }

  const enabledOneClickPayment =
    api.computedConfig.oneClickPayment && !process.env.IS_CREDIT_CARD_IFRAME
  const createPaymentSource = formData.create_payment_source
  const setCreatePaymentSource = (value: boolean) =>
    (formData.create_payment_source = value)

  const creditCardNumberMaskProps = {
    mask: getCreditCardNumberMask(cardno, form.getPaymentConfig().flow),
    onAccept: (value, mask) => setCardno(mask.unmaskedValue),
  }

  const expMaskProps = {
    mask: 'M / Y',
    blocks: {
      M: {
        mask: IMask.MaskedRange,
        from: 1,
        to: 12,
        maxLength: 2,
      },
      Y: {
        mask: IMask.MaskedRange,
        from: parseInt(new Date().getFullYear().toString().substr(-2)),
        to: 99,
        maxLength: 2,
      },
    },
    onAccept: (value, mask) => setExp(mask.unmaskedValue),
  }

  const cvvMaskProps = {
    mask: '000[0]',
    onAccept: (value, mask) => setCvv(mask.unmaskedValue),
  }

  const infoToggleTitleCvv = api.trans('info_toggle.cvv.title', {
    defaultValue: null,
  })
  const infoToggleTextHTMLCvv = api.trans('info_toggle.cvv.text_html', {
    defaultValue: null,
  })
  const [infoToggleExpandedCvv, setInfoToggleExpandedCvv] = useState(false)

  const infoToggleTitleCc = api.trans('info_toggle.cc.title', {
    defaultValue: null,
  })
  const infoToggleTextHTMLCc = api.trans('info_toggle.cc.text_html', {
    defaultValue: null,
  })

  let cnCardno = ['rnw-col-12']
  let cnExp = ['rnw-col-12', 'rnw-col-xs-6']
  let cnCvv = ['rnw-col-12', 'rnw-col-xs-6']

  if (skipCvv) {
    cnCardno = ['rnw-col-12', 'rnw-col-xs-6']
    cnExp = ['rnw-col-12', 'rnw-col-xs-6']
  }

  return (
    <div className={cn('payment-method-form', PaymentMethod.CREDIT_CARD)}>
      <DynamicSlot name={DynamicSlotName.CREDIT_CARD_FORM_START} form={form} />

      <div className="main">
        <div className="rnw-row">
          <div className={cn(cnCardno)}>
            <div className="form-block cardno">
              <div className="field-block">
                <label
                  className="label-block"
                  htmlFor={api.ui.generateUniqueElementId('cardno')}
                >
                  {api.trans('payment_form.cardno')}
                </label>
                <InputWrapper form={form} fields="cardno">
                  <DirtyWrapper form={form} fields="cardno">
                    <IMaskInput
                      {...creditCardNumberMaskProps}
                      type="text"
                      inputMode="numeric"
                      data-pattern="[0-9]*"
                      id={api.ui.generateUniqueElementId('cardno')}
                      aria-describedby={api.ui.generateUniqueElementId(
                        'cardno-error',
                      )}
                      className="input-text"
                      name="cardno"
                      placeholder={api.trans(
                        'payment_form.placeholders.cardno',
                      )}
                      value={cardno}
                      autoComplete="cc-number"
                    />
                  </DirtyWrapper>
                </InputWrapper>
              </div>

              <ErrorMessage
                errors={errors}
                errorKey="cardno"
                id={api.ui.generateUniqueElementId('cardno-error')}
              />
            </div>
          </div>

          <div className={cn(cnExp)}>
            <div className="form-block exp">
              <div className="field-block">
                <label
                  className="label-block"
                  htmlFor={api.ui.generateUniqueElementId('exp')}
                >
                  {api.trans('payment_form.exp')}
                </label>
                <InputWrapper form={form} fields={['expm', 'expy']}>
                  <DirtyWrapper form={form} fields={['expm', 'expy']}>
                    <IMaskInput
                      {...expMaskProps}
                      type="text"
                      inputMode="numeric"
                      data-pattern="[0-9]*"
                      id={api.ui.generateUniqueElementId('exp')}
                      aria-describedby={
                        api.ui.generateUniqueElementId('expm-error') +
                        ' ' +
                        api.ui.generateUniqueElementId('expy-error')
                      }
                      className="input-text"
                      name="exp"
                      placeholder={api.trans('payment_form.placeholders.exp')}
                      value={exp}
                      autoComplete="cc-exp"
                    />
                  </DirtyWrapper>
                </InputWrapper>
              </div>

              <ErrorMessage
                errors={errors}
                errorKey="expm"
                id={api.ui.generateUniqueElementId('expm-error')}
              />
              <ErrorMessage
                errors={errors}
                errorKey="expy"
                id={api.ui.generateUniqueElementId('expy-error')}
              />
            </div>
          </div>

          {!skipCvv && (
            <div className={cn(cnCvv)}>
              <div
                className={cn('form-block', 'cvv', {
                  'has-info-button': infoToggleTextHTMLCvv,
                })}
              >
                <div className="field-block">
                  <label
                    className="label-block"
                    htmlFor={api.ui.generateUniqueElementId('cvv')}
                  >
                    {api.trans('payment_form.cvv')}
                  </label>
                  <InputWrapper form={form} fields="cvv">
                    <DirtyWrapper form={form} fields="cvv">
                      <IMaskInput
                        {...cvvMaskProps}
                        type="text"
                        inputMode="numeric"
                        data-pattern="[0-9]*"
                        id={api.ui.generateUniqueElementId('cvv')}
                        aria-describedby={api.ui.generateUniqueElementId(
                          'cvv-error',
                        )}
                        className="input-text"
                        name="cvv"
                        placeholder={api.trans('payment_form.placeholders.cvv')}
                        value={cvv}
                        autoComplete="cc-csc"
                      />
                    </DirtyWrapper>
                  </InputWrapper>
                  <div className="icons-block">
                    {infoToggleTextHTMLCvv && (
                      <button
                        type="button"
                        aria-label={infoToggleTitleCvv}
                        onClick={() => setInfoToggleExpandedCvv((v) => !v)}
                        aria-expanded={infoToggleExpandedCvv}
                        aria-controls={api.ui.generateUniqueElementId(
                          'info-toggle-cvv',
                        )}
                        className={cn('info-button', {
                          expanded: infoToggleExpandedCvv,
                          collapsed: !infoToggleExpandedCvv,
                        })}
                      >
                        <span className="icon">
                          <IconInfo />
                        </span>
                      </button>
                    )}
                  </div>
                </div>

                <ErrorMessage
                  errors={errors}
                  errorKey="cvv"
                  id={api.ui.generateUniqueElementId('cvv-error')}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <InfoToggle
        idKey="info-toggle-cvv"
        className={cn('info-toggle-pm', 'info-toggle-cvv')}
        textHTML={infoToggleTextHTMLCvv}
        expanded={infoToggleExpandedCvv}
      />

      <InfoToggle
        idKey="info-toggle-cc"
        className={cn('info-toggle-pm', 'info-toggle-cc')}
        title={infoToggleTitleCc}
        textHTML={infoToggleTextHTMLCc}
      />

      {enabledOneClickPayment &&
        (() => {
          let infoId = api.ui.generateUniqueElementId(
            'create_payment_source-info',
          )
          let infoHTML = api.trans('blocks.one_click_payment.text_html')

          return (
            <div className="form-block create_payment_source">
              <div className="field-block">
                <RippleWrapper disabled>
                  <label className="label-checkbox-block">
                    <InputCheckboxWrapper
                      form={form}
                      fields="create_payment_source"
                    >
                      <DirtyWrapper form={form} fields="create_payment_source">
                        <input
                          type="checkbox"
                          aria-describedby={infoId}
                          className="input-checkbox"
                          name="create_payment_source"
                          checked={createPaymentSource}
                          onChange={(e) =>
                            setCreatePaymentSource(e.target.checked)
                          }
                        />
                      </DirtyWrapper>
                    </InputCheckboxWrapper>
                    <div>
                      <span>{api.trans('blocks.one_click_payment.label')}</span>
                    </div>
                  </label>
                </RippleWrapper>

                {!!infoHTML && (
                  <div className="footer">
                    <div
                      className="text"
                      dangerouslySetInnerHTML={{__html: infoHTML}}
                      id={infoId}
                    />
                  </div>
                )}
              </div>
            </div>
          )
        })()}

      <DynamicSlot name={DynamicSlotName.CREDIT_CARD_FORM_END} form={form} />
    </div>
  )
})
